/** "+0.25" / "−0.15" / "+0.00", on the score's scale. */
export const signed = (n: number) => `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(2)}`;

const r2 = (n: number) => Math.round(n * 100) / 100;
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

export interface WhyInput {
  /** Points per part, as the API scored them. */
  parts: { name: number; domain: number; country: number; nameNoise: number };
  score: number;
  nameSimilarity: number | null;
  /** Domain part in words ("same domain"), or null. */
  domainNote: string | null;
  /** Country part in words with its points ("same country +0.10"), or null. */
  countryNote: string | null;
  /** The country note the header states once for most rows. */
  commonCountry: string | null;
}

/**
 * The Why column as one sum that adds up, as displayed, to the score it ends
 * on ("name +0.75 · same domain +0.25 · = 1.00"), so the number in the Score
 * column is never read as a part of it.
 *
 * The country part most rows share is said in the header and left off a row
 * only when leaving it off changes nothing (the 1.00 cap already absorbs it).
 * The cap or floor is then computed from the parts the row shows, so a row
 * never reads "0.75 + 0.25, capped −0.10 = 1.00" (review #11, 13 Oct 2026).
 */
export function whyClauses(w: WhyInput): string[] {
  const base = w.parts.name + w.parts.domain + w.parts.nameNoise;
  const hideCountry =
    !!w.countryNote && w.countryNote === w.commonCountry && r2(clamp01(base)) === r2(w.score);
  const showCountry = !!w.countryNote && !hideCountry;
  const shown = base + (showCountry ? w.parts.country : 0);
  const adjust = r2(w.score - shown);
  return [
    w.nameSimilarity !== null
      ? `name ${signed(w.parts.name)} (similarity ${w.nameSimilarity.toFixed(2)} × 0.75)`
      : `name ${signed(w.parts.name)}`,
    w.domainNote ? `${w.domainNote} ${signed(w.parts.domain)}` : null,
    showCountry ? w.countryNote : null,
    w.parts.nameNoise ? `names agree only without “The”/“Group” ${signed(w.parts.nameNoise)}` : null,
    adjust < 0 ? `capped at 1.00 ${signed(adjust)}` : adjust > 0 ? `floored at 0 ${signed(adjust)}` : null,
    `= ${w.score.toFixed(2)}`,
  ].filter((x): x is string => !!x);
}
